import {
  AISettings,
  FridgeAnalysisResult,
} from '../types/vision';
import { AIScanResult, DetectedItem, GroceryItem } from '../types';
import { getDateOffset } from './mockData';

/**
 * Executes vision analysis by calling the secure Cloudflare serverless endpoint /api/analyze
 */
export async function analyzeFridgePhoto(
  imageDataUrl: string,
  settings: AISettings,
  passcode = 'refrigerator'
): Promise<FridgeAnalysisResult> {
  const provider = settings.provider || 'gemini';
  const model = provider === 'groq' ? settings.groqModel : settings.geminiModel;

  try {
    const res = await fetch('/api/analyze', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        imageDataUrl,
        passcode,
        provider,
        model,
        customPrompt: settings.customPrompt,
      }),
    });

    if (res.ok) {
      return await res.json();
    }

    const errData = await res.json().catch(() => ({ error: `Server error HTTP ${res.status}` }));
    throw new Error(errData.error || `Server Error ${res.status}`);
  } catch (err: any) {
    console.error('Vision analysis request failed', err);
    throw err;
  }
}

export async function analyzeGroceryImage(
  imageDataUrl: string,
  _catalog?: GroceryItem[],
  _apiKey?: string,
  _presetIndex?: number
): Promise<AIScanResult> {
  const fridgeResult = await analyzeFridgePhoto(imageDataUrl, {
    provider: 'gemini',
    geminiApiKey: '',
    geminiModel: 'gemini-1.5-flash',
    groqApiKey: '',
    groqModel: 'llama-3.2-11b-vision-preview',
  });
  const detectedItems: DetectedItem[] = fridgeResult.detectedItems.map((it) => ({
    id: it.id,
    name: it.name,
    brand: it.brand,
    category: it.category as any,
    suggestedLocationId: 'loc-fridge',
    quantity: it.quantity,
    unit: it.unit,
    estimatedShelfLifeDays: it.estimatedShelfLifeDays,
    confidence: it.confidence,
    expirationDate: getDateOffset(it.estimatedShelfLifeDays),
    selected: true,
  }));

  return {
    detectedItems,
    overallSummary: fridgeResult.summary,
    recognizedImagePreview: imageDataUrl,
  };
}

export async function lookupBarcodeOpenFoodFacts(barcode: string): Promise<{
  name: string;
  brand?: string;
  category?: string;
  imageUrl?: string;
} | null> {
  try {
    const res = await fetch(
      `https://world.openfoodfacts.org/api/v0/product/${encodeURIComponent(barcode)}.json`
    );
    if (res.ok) {
      const data = await res.json();
      if (data.status === 1 && data.product) {
        const p = data.product;
        return {
          name: p.product_name || p.generic_name || 'Scanned Food Product',
          brand: p.brands || undefined,
          category: 'Produce',
          imageUrl: p.image_front_url || p.image_url || undefined,
        };
      }
    }
  } catch (e) {
    console.warn('OpenFoodFacts lookup failed', e);
  }
  return null;
}
